import type { CanvasObject } from "@refinement-canvas/domain";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "./canvas-coordinates";
import {
  STICKY_FONT_MAX_PX,
  STICKY_FONT_MIN_PX,
  STICKY_LINE_HEIGHT_RATIO,
  STICKY_PADDING_PX,
  STICKY_SIGNATURE_RESERVE_PX,
  computeBestFitFontSize,
} from "./sticky-font-fit";
import { arrowHeadPoints, computeConnectorRoute } from "./connector-geometry";

// PDF 出力用に、キャンバス全体を高解像度の PNG として描画する。
// 既存サムネイル描画（renderCanvasThumbnailDataUrl）の高解像度版で、画像 / 動画オブジェクトについては
// 利用可能であれば画像本体を読み込んで貼り付ける（読み込み失敗時は塗りつぶしのプレースホルダで代替）。
//
// 戻り値: data URL（image/png）。失敗時は空文字列。

const EXPORT_WIDTH_PX = 3508; // A4 横 300DPI 相当（約 3508x1973）。16:9 で描画する。
const EXPORT_HEIGHT_PX = Math.round((CANVAS_HEIGHT_PX / CANVAS_WIDTH_PX) * EXPORT_WIDTH_PX);
const IMAGE_LOAD_TIMEOUT_MS = 4_000;

export async function renderCanvasExportImage(
  objects: readonly CanvasObject[]
): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = EXPORT_WIDTH_PX;
  canvas.height = EXPORT_HEIGHT_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const scaleX = EXPORT_WIDTH_PX / CANVAS_WIDTH_PX;
  const scaleY = EXPORT_HEIGHT_PX / CANVAS_HEIGHT_PX;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawGrid(ctx);

  // 画像オブジェクト分のみ先に Image を読み込み、ペイント順序を維持するため zOrder で整列する。
  const ordered = objects.slice().sort((a, b) => a.zOrder - b.zOrder);
  const imageBySrc = await preloadImages(ordered);
  // コネクタは端点オブジェクトの現在位置から都度ルーティングするため id 索引を渡す。
  const objectsById = new Map(objects.map((object) => [object.id, object]));

  for (const object of ordered) {
    ctx.save();
    ctx.translate((object.x + object.w / 2) * scaleX, (object.y + object.h / 2) * scaleY);
    ctx.rotate(((object.rotation ?? 0) * Math.PI) / 180);
    ctx.translate((-object.w / 2) * scaleX, (-object.h / 2) * scaleY);
    drawObject(ctx, object, scaleX, scaleY, imageBySrc, objectsById);
    ctx.restore();
  }

  return canvas.toDataURL("image/png");
}

/**
 * 既に開いている印刷用ウィンドウへ、data URL 化した画像を含む HTML を書き込み印刷ダイアログを呼び出す。
 * 利用者は印刷先に「PDF として保存」を選び、PDF ファイルとして書き出す。
 *
 * ウィンドウはポップアップブロックを避けるため、呼び出し元のクリック同期処理で先に
 * `window.open()` して渡す（user activation が有効なうちに開く）。
 * 連打防止は呼び出し元（AppMenu の busy 状態）で行う。
 */
export function openPrintWindowWithImage(
  printWindow: Window,
  dataUrl: string,
  title: string
): void {
  if (!dataUrl) {
    printWindow.close();
    return;
  }
  const sanitizedTitle = escapeHtml(title);
  // ページに対して画像を全幅で配置し、横向き印刷で 1 枚に収まりやすいよう @page を指定する。
  // 印刷後はウィンドウを自動で閉じる。
  const html = `<!doctype html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>${sanitizedTitle}</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { display: flex; align-items: center; justify-content: center; min-height: 100vh; }
  img { width: 100%; height: auto; max-width: 100%; }
  @media print {
    body { min-height: 0; }
    img { width: 100%; height: auto; page-break-inside: avoid; }
  }
</style>
</head>
<body>
<img src="${dataUrl}" alt="${sanitizedTitle}" onload="setTimeout(function(){window.focus();window.print();}, 50);" />
<script>
window.addEventListener('afterprint', function(){ window.close(); });
</script>
</body>
</html>`;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}

async function preloadImages(
  objects: readonly CanvasObject[]
): Promise<Map<string, HTMLImageElement>> {
  const srcs = new Set<string>();
  for (const obj of objects) {
    if (obj.type === "image" && obj.src) srcs.add(obj.src);
    if (obj.type === "link-card" && obj.imageUrl) srcs.add(obj.imageUrl);
  }
  const entries = await Promise.all(
    Array.from(srcs).map(async (src) => {
      const img = await loadImage(src);
      return img ? ([src, img] as const) : null;
    })
  );
  const map = new Map<string, HTMLImageElement>();
  for (const entry of entries) {
    if (entry) map.set(entry[0], entry[1]);
  }
  return map;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    // CORS の絡みで toDataURL がブロックされるリスクを下げるため crossOrigin を付ける。
    // 同一オリジン or 公開バケットからの取得を想定する。失敗時はプレースホルダにフォールバックする。
    img.crossOrigin = "anonymous";
    const timer = window.setTimeout(() => resolve(null), IMAGE_LOAD_TIMEOUT_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
    };
    img.src = src;
  });
}

function drawGrid(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = "rgba(26, 26, 46, 0.08)";
  for (let x = 0; x < EXPORT_WIDTH_PX; x += 80) {
    for (let y = 0; y < EXPORT_HEIGHT_PX; y += 80) {
      ctx.fillRect(x, y, 2, 2);
    }
  }
}

function drawObject(
  ctx: CanvasRenderingContext2D,
  object: CanvasObject,
  scaleX: number,
  scaleY: number,
  imageBySrc: Map<string, HTMLImageElement>,
  objectsById: Map<string, CanvasObject>
): void {
  const w = Math.max(1, object.w * scaleX);
  const h = Math.max(1, object.h * scaleY);
  switch (object.type) {
    case "sticky":
      ctx.fillStyle = object.color ?? "#fff5b3";
      ctx.strokeStyle = "rgba(30, 41, 59, 0.4)";
      ctx.lineWidth = 1;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeRect(0, 0, w, h);
      // 本文・署名は付箋の枠でクリップしてから描画する（画面側 sticky-object.tsx の overflow:hidden と同等）。
      // 極小サイズ（padding と署名領域を引くと描画可能領域が 0 以下になる）付箋でも、PDF が枠外へ
      // はみ出して描画されないようにする。
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      if (object.text) {
        // 画面表示（sticky-object.tsx）と同様に、付箋サイズへ収まる文字サイズを自動算出する。
        const padX = STICKY_PADDING_PX * scaleX;
        const padY = STICKY_PADDING_PX * scaleY;
        // updatedByName がある付箋は、画面と同じく下部に署名領域を確保する（本文と署名が干渉しないようにする）。
        const reserveY = object.updatedByName ? STICKY_SIGNATURE_RESERVE_PX * scaleY : 0;
        const availWidth = w - padX * 2;
        const availHeight = h - padY * 2 - reserveY;
        const fontPx = computeStickyExportFontPx(ctx, object.text, availWidth, availHeight, scaleY);
        const lineHeight = fontPx * STICKY_LINE_HEIGHT_RATIO;
        drawWrappedText(ctx, object.text, padX, padY + fontPx, availWidth, lineHeight, {
          color: "#1a1a2e",
          fontPx,
          align: object.textAlign ?? "left",
        });
      }
      // 画面表示（sticky-object.tsx の左下署名）と見た目を揃えるため、最終更新者名を PDF にも描画する。
      // これにより、上で確保した下部の署名領域（reserveY）が無駄な空白にならず、出力が画面と一致する。
      if (object.updatedByName) {
        drawStickySignature(ctx, object.updatedByName, w, h, scaleX, scaleY);
      }
      ctx.restore();
      return;
    case "shape":
      ctx.strokeStyle = object.stroke ?? "#1a1a2e";
      ctx.fillStyle = object.fill ?? "rgba(255,255,255,0)";
      ctx.lineWidth = Math.max(1, 2 * Math.min(scaleX, scaleY));
      drawShape(ctx, object.shapeKind, w, h);
      return;
    case "text":
      ctx.fillStyle = object.color ?? "#1a1a2e";
      ctx.font = `${Math.max(10, (object.fontSize ?? 16) * scaleY)}px sans-serif`;
      drawWrappedText(
        ctx,
        object.text,
        0,
        Math.max(12, (object.fontSize ?? 16) * scaleY),
        w,
        Math.max(14, (object.fontSize ?? 16) * scaleY * 1.4),
        {
          color: object.color ?? "#1a1a2e",
          fontPx: Math.max(10, (object.fontSize ?? 16) * scaleY),
          align: object.textAlign ?? "left",
        }
      );
      return;
    case "image": {
      const img = object.src ? imageBySrc.get(object.src) : undefined;
      if (img) {
        ctx.drawImage(img, 0, 0, w, h);
      } else {
        drawMediaPlaceholder(ctx, w, h, "#dbeafe");
      }
      return;
    }
    case "video":
      drawMediaPlaceholder(ctx, w, h, "#111827");
      return;
    case "link-card": {
      drawMediaPlaceholder(ctx, w, h, "#eef2ff");
      const cardImg = object.imageUrl ? imageBySrc.get(object.imageUrl) : undefined;
      if (cardImg) {
        const cardW = w;
        const cardH = h * 0.55;
        ctx.drawImage(cardImg, 0, 0, cardW, cardH);
      }
      ctx.fillStyle = "#1e3a8a";
      ctx.font = `${Math.max(10, 14 * scaleY)}px sans-serif`;
      ctx.fillText(object.title.slice(0, 64), 8 * scaleX, Math.min(h - 8, h * 0.65));
      return;
    }
    case "pen-stroke":
      ctx.strokeStyle = object.color;
      ctx.lineWidth = Math.max(1, object.thickness * scaleX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      object.points.forEach((point, index) => {
        const x = (point.x - object.x) * scaleX;
        const y = (point.y - object.y) * scaleY;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      return;
    case "stamp": {
      // スタンプは絵文字グリフを中央に描画する。フォントは OS のカラー絵文字に委ねる
      // （Canvas2D の描画結果はモノクロになる環境もあるが、PNG 化前提のため最低限読める形にする）。
      const glyph = STAMP_PDF_GLYPH[object.stampKind];
      const sizePx = Math.max(12, Math.min(w, h) * 0.85);
      ctx.fillStyle = "#1a1a2e";
      ctx.font = `${sizePx}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(glyph, w / 2, h / 2);
      // テキスト系設定をリセットして以降の描画へ影響しないようにする。
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
      return;
    }
    case "connector": {
      const fromObj = objectsById.get(object.from.objectId);
      const toObj = objectsById.get(object.to.objectId);
      if (!fromObj || !toObj) return;
      const route = computeConnectorRoute(
        { x: fromObj.x, y: fromObj.y, w: fromObj.w, h: fromObj.h },
        object.from.side,
        { x: toObj.x, y: toObj.y, w: toObj.w, h: toObj.h },
        object.to.side
      );
      if (route.length < 2) return;
      // ルートはステージ座標。ctx は object.x/object.y に平行移動済みのため、相対座標へ変換する。
      const toLocal = (p: { x: number; y: number }): { x: number; y: number } => ({
        x: (p.x - object.x) * scaleX,
        y: (p.y - object.y) * scaleY,
      });
      ctx.strokeStyle = object.stroke ?? "#475569";
      ctx.lineWidth = Math.max(1, 2 * Math.min(scaleX, scaleY));
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      route.forEach((point, index) => {
        const l = toLocal(point);
        if (index === 0) ctx.moveTo(l.x, l.y);
        else ctx.lineTo(l.x, l.y);
      });
      ctx.stroke();
      const tip = toLocal(route[route.length - 1]!);
      const before = toLocal(route[route.length - 2]!);
      const head = arrowHeadPoints(tip, before, 12 * Math.min(scaleX, scaleY));
      if (head) {
        ctx.beginPath();
        ctx.moveTo(head.left.x, head.left.y);
        ctx.lineTo(tip.x, tip.y);
        ctx.lineTo(head.right.x, head.right.y);
        ctx.stroke();
      }
      return;
    }
  }
}

// PDF / 印刷出力で使うスタンプ → 絵文字グリフのマッピング。
// 画面表示用（stamp-object.tsx の STAMP_KIND_GLYPH）と同じ値を持つが、
// 循環参照を避けるためにこのモジュール内で複製している。
const STAMP_PDF_GLYPH: Record<"thumbs-up" | "fire" | "smile" | "hundred" | "plus", string> = {
  "thumbs-up": "👍",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
};

function drawShape(
  ctx: CanvasRenderingContext2D,
  shapeKind: "arrow" | "circle" | "rectangle",
  w: number,
  h: number
): void {
  if (shapeKind === "circle") {
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, Math.max(1, w / 2 - 2), Math.max(1, h / 2 - 2), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (shapeKind === "arrow") {
    ctx.beginPath();
    ctx.moveTo(4, h / 2);
    ctx.lineTo(w - 12, h / 2);
    ctx.moveTo(w - 12, h / 2 - 6);
    ctx.lineTo(w - 4, h / 2);
    ctx.lineTo(w - 12, h / 2 + 6);
    ctx.stroke();
    return;
  }
  ctx.fillRect(2, 2, w - 4, h - 4);
  ctx.strokeRect(2, 2, w - 4, h - 4);
}

function drawMediaPlaceholder(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  color: string
): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = "rgba(30, 41, 59, 0.35)";
  ctx.lineWidth = 1;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}

/**
 * 簡易の折り返し描画。日本語/英語混在で軽くワードラップする。
 */
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  style: {
    readonly color: string;
    readonly fontPx: number;
    readonly align?: CanvasTextAlign | undefined;
  }
): void {
  ctx.fillStyle = style.color;
  ctx.font = `${style.fontPx}px sans-serif`;
  // 文字寄せ（未指定は左寄せ）。テキストボックス [x, x+maxWidth] 内でアンカー位置を切り替える。
  const align = style.align ?? "left";
  ctx.textAlign = align;
  const anchorX = align === "center" ? x + maxWidth / 2 : align === "right" ? x + maxWidth : x;
  const lines = wrapTextToLines(ctx, text, maxWidth);
  let cursorY = y;
  for (const line of lines) {
    ctx.fillText(line, anchorX, cursorY);
    cursorY += lineHeight;
  }
  // 後続オブジェクトの描画へ寄せ設定を漏らさないよう既定へ戻す。
  ctx.textAlign = "left";
}

/**
 * 付箋の最終更新者名（左下の署名）を PDF へ描画する。
 * 画面表示（sticky-object.tsx）と同じレイアウト定数（左 8px / 下 4px / 右 32px / 11px / 半透明）に揃え、
 * 幅を超える場合は省略記号（…）で切り詰める。
 */
function drawStickySignature(
  ctx: CanvasRenderingContext2D,
  name: string,
  w: number,
  h: number,
  scaleX: number,
  scaleY: number
): void {
  const sigFontPx = 11 * scaleY;
  ctx.font = `${sigFontPx}px sans-serif`;
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.textAlign = "left";
  const left = 8 * scaleX;
  // めくれ三角は PDF では描画しないが、画面と同じ折り返し幅にするため右マージン 32px を確保する。
  const right = 32 * scaleX;
  const maxWidth = Math.max(0, w - left - right);
  const text = truncateToWidth(ctx, name, maxWidth);
  if (text.length === 0) return;
  // bottom:4px 相当。alphabetic baseline で下端から少し上に置き、descender が枠外へ出ないようにする。
  ctx.fillText(text, left, h - 5 * scaleY);
}

/**
 * 現在の ctx.font 設定のもとで、テキストが maxWidth に収まるよう末尾を省略記号（…）で切り詰める。
 * 呼び出し前に ctx.font を設定しておくこと。
 */
function truncateToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0) return "";
  if (ctx.measureText(text).width <= maxWidth) return text;
  const ellipsis = "…";
  let result = text;
  while (result.length > 0 && ctx.measureText(result + ellipsis).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return result.length > 0 ? result + ellipsis : ellipsis;
}

/**
 * 日本語 / 英語混在のテキストを、現在の ctx.font 設定のもとで maxWidth に収まるよう折り返した行配列にする。
 * 改行文字（\n / \r\n）でも分割する。呼び出し前に ctx.font を設定しておくこと。
 */
function wrapTextToLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const lines: string[] = [];
  const rawLines = text.split(/\r?\n/);
  for (const rawLine of rawLines) {
    let current = "";
    for (const ch of rawLine) {
      const candidate = current + ch;
      if (ctx.measureText(candidate).width > maxWidth && current.length > 0) {
        lines.push(current);
        current = ch;
      } else {
        current = candidate;
      }
    }
    lines.push(current);
  }
  return lines;
}

/**
 * PDF 出力時の付箋本文に使う文字サイズ（スケール後 px）を、利用可能領域へちょうど収まるよう求める。
 * 画面表示（sticky-font-fit.ts）と同じ二分探索アルゴリズムを共有し、Canvas 2D の measureText で計測する。
 * 戻り値はスケール後の px（drawWrappedText / ctx.font へそのまま渡せる）。
 */
function computeStickyExportFontPx(
  ctx: CanvasRenderingContext2D,
  text: string,
  availWidth: number,
  availHeight: number,
  scaleY: number
): number {
  if (availWidth <= 0 || availHeight <= 0) return STICKY_FONT_MIN_PX * scaleY;
  const fits = (fontPx: number): boolean => {
    const scaledFontPx = fontPx * scaleY;
    ctx.font = `${scaledFontPx}px sans-serif`;
    const lines = wrapTextToLines(ctx, text, availWidth);
    if (lines.length * scaledFontPx * STICKY_LINE_HEIGHT_RATIO > availHeight) return false;
    // 横幅も判定する。wrapTextToLines は 1 文字目だけで availWidth を超える場合でもその文字を
    // 1 行として返すため、高さだけ見ると「実際は横にはみ出すサイズ」を採用してしまう
    // （画面側 measureStickyFitFontSize は scrollWidth で同等の判定をしている）。
    for (const line of lines) {
      if (ctx.measureText(line).width > availWidth) return false;
    }
    return true;
  };
  const unscaled = computeBestFitFontSize({
    minPx: STICKY_FONT_MIN_PX,
    maxPx: STICKY_FONT_MAX_PX,
    fits,
  });
  return unscaled * scaleY;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
